// Native blocking: expose blocked users state and helpers for components
import { Capacitor } from '@capacitor/core';
import { useEffect, useMemo, useState } from 'react';

import { useWebsite } from '../../../front/src/hooks/useWebsite';
import type { BlockedUser } from '../services/capacitor/blockedUsers';
import {
  blockUser as blockUserService,
  getBlockedUsers,
  unblockUser as unblockUserService,
} from '../services/capacitor/blockedUsers';

// Native blocking: custom event to notify components when blocking state changes
const BLOCKED_USERS_UPDATED_EVENT = 'blocked-users-updated';

const dispatchBlockedUsersUpdated = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(BLOCKED_USERS_UPDATED_EVENT));
  }
};

type BlockParams = {
  userId: string;
  pseudo?: string;
  picture?: string;
};

export const useBlockedUsers = () => {
  const { id: websiteKey } = useWebsite();
  const isNative = Capacitor.isNativePlatform();

  const [blockedUsers, setBlockedUsers] = useState<BlockedUser[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  // Native blocking: version counter to force rerender when blocking state changes
  const [updateVersion, setUpdateVersion] = useState<number>(0);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setIsLoading(true);
      const users = await getBlockedUsers(websiteKey || '');
      if (!mounted) return;
      setBlockedUsers(users);
      setIsLoading(false);
    };
    if (isNative && websiteKey) {
      load();
    } else {
      setBlockedUsers([]);
      setIsLoading(false);
    }
    return () => {
      mounted = false;
    };
  }, [isNative, websiteKey]);

  // Native blocking: listen to blocked users update events to reload state
  useEffect(() => {
    if (!isNative || !websiteKey) return;

    const handleUpdate = async () => {
      const users = await getBlockedUsers(websiteKey);
      setBlockedUsers(users);
      setUpdateVersion(prev => prev + 1);
    };

    window.addEventListener(BLOCKED_USERS_UPDATED_EVENT, handleUpdate);
    return () => {
      window.removeEventListener(BLOCKED_USERS_UPDATED_EVENT, handleUpdate);
    };
  }, [isNative, websiteKey]);

  const blockedUserIds = useMemo(() => new Set(blockedUsers.map(u => u.userId)), [blockedUsers]);

  const isBlocked = (userId?: string | null) => {
    if (!isNative) return false;
    if (!userId) return false;
    return blockedUserIds.has(userId);
  };

  const block = async ({ userId, pseudo, picture }: BlockParams) => {
    if (!isNative || !websiteKey || !userId) return;
    await blockUserService(websiteKey, { userId, pseudo, picture });
    setBlockedUsers(prev =>
      prev.some(u => u.userId === userId)
        ? prev
        : [...prev, { userId, pseudo, picture, blockedAt: Date.now() }],
    );
    // Native blocking: increment version to force rerender
    setUpdateVersion(prev => prev + 1);
    // Native blocking: dispatch event to notify all components
    dispatchBlockedUsersUpdated();
  };

  const unblock = async (userId: string) => {
    if (!isNative || !websiteKey || !userId) return;
    await unblockUserService(websiteKey, userId);
    setBlockedUsers(prev => prev.filter(u => u.userId !== userId));
    // Native blocking: increment version to force rerender
    setUpdateVersion(prev => prev + 1);
    // Native blocking: dispatch event to notify all components
    dispatchBlockedUsersUpdated();
  };

  return {
    blockedUsers,
    blockedUserIds,
    isBlocked,
    block,
    unblock,
    isLoading,
    isNative,
    updateVersion, // Native blocking: expose version to force rerender in components
  };
};
